import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, ReplaySubject, catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { User, UserPermissions, UserRole } from '../../models';
import { AuthorizationService } from '../../services/authorization.service';

const TOKEN_KEY = 'ycc_access_token';

/** Réponse de POST /auth/login et POST /auth/refresh-token (identity-service, via gateway). */
interface TokenResponse {
  tokenType: string;
  accessToken: string;
  expiresAt: string;
  userId: string;
  roles: string[];
}

/** Réponse de GET /auth/me/bootstrap : contexte de démarrage de l'app (identité + session). */
interface BootstrapResponse {
  userId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  accountType: string;
  status: string;
  roles: string[];
}

/** Rôles plateforme (back-office) au sens strict — distincts de PARTNER_ADMIN, qui désigne un partenaire. */
const PLATFORM_ADMIN_ROLES = ['ADMIN', 'SUPER_ADMIN', 'TECHNICAL_ADMIN', 'PROGRAM_ADMIN'];

/**
 * Mappe accountType/roles backend vers les rôles applicatifs du front. Compare les rôles un par un
 * (pas une recherche de sous-chaîne sur le tout concaténé) : "PARTNER_ADMIN" contient "ADMIN" et
 * serait sinon classé comme rôle admin plateforme, empêchant tout partenaire d'atteindre /espace-partenaire.
 */
function toUserRole(accountType: string, roles: string[]): UserRole {
  const upperAccountType = accountType.toUpperCase();
  const upperRoles = roles.map((role) => role.toUpperCase());
  if (upperAccountType === 'ADMIN') return 'admin';
  if (upperRoles.some((role) => PLATFORM_ADMIN_ROLES.includes(role))) return 'admin';

  const haystack = [upperAccountType, ...upperRoles].join(' ');
  if (/PARTNER|STARTUP|TPME|MENTOR|EVALUATOR/.test(haystack)) return 'partenaire';
  return 'jeune';
}

/** roles = ceux d'authorization-service si disponibles (autoritaires), sinon repli sur ceux du JWT. */
function toUser(res: BootstrapResponse, roles: string[]): User {
  return {
    id: res.userId,
    email: res.email ?? res.phone ?? '',
    phone: res.phone ?? undefined,
    accountType: res.accountType,
    role: toUserRole(res.accountType, roles),
    firstName: res.firstName ?? '',
    lastName: res.lastName ?? '',
    createdAt: '',
  };
}

/**
 * Contrat de POST /auth/register (identity-service). Inscription publique : YOUNG, BENEFICIARY,
 * STARTUP ou TPME (PARTNER en est exclu — réservé à la création par un administrateur, voir
 * IdentityService.PUBLIC_REGISTRATION_TYPES et [[account-type-creation-channels]]).
 */
export interface RegisterPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  password: string;
  accountType: 'YOUNG' | 'BENEFICIARY' | 'STARTUP' | 'TPME';
  preferredLanguage?: string;
  regionCode?: string;
  /**
   * STARTUP/TPME uniquement — identity-service publie ces champs dans l'événement
   * identity.user-created, que profile-service consomme pour pré-remplir automatiquement la fiche
   * organisation brouillon (ProfileService.ensureOrganizationDraft) : plus besoin de les re-saisir
   * ni de les faire transiter par un pont localStorage côté frontend (voir historique register.ts).
   */
  organizationName?: string;
  organizationType?: string;
  representativeName?: string;
  termsAccepted: boolean;
}

/** Réponse générique de l'identity-service */
export interface ApiMessageResponse {
  message: string;
}

/** Réponse de GET /auth/activation-token/status et POST /auth/activation-token/verify-before-submit. Ne consomme pas le token. */
export interface ActivationTokenCheck {
  valid: boolean;
  expired?: boolean;
  consumed?: boolean;
  nextAction?: string;
}

/** Réponse de GET /auth/me/security-summary. */
export interface SecuritySummary {
  userId: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  status: string;
  failedLoginCount: number;
  lastLoginAt: string | null;
  activeSessions: number;
  lockedAt: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  // identity-service est joint via le gateway sous ce préfixe
  private readonly baseUrl = `${environment.apiUrl}/identity-service/api/v1/auth`;

  private readonly currentUserSignal = signal<User | null>(null);
  private readonly permissionsSignal = signal<UserPermissions | null>(null);
  private readonly sessionRestoredSubject = new ReplaySubject<boolean>(1);

  readonly currentUser = this.currentUserSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.currentUserSignal() !== null);
  readonly role = computed<UserRole | null>(() => this.currentUserSignal()?.role ?? null);
  /** Permissions fines (authorization-service) de l'utilisateur connecté ; null tant qu'elles n'ont pas encore été chargées. */
  readonly permissions = this.permissionsSignal.asReadonly();
  readonly sessionRestored$ = this.sessionRestoredSubject.asObservable();

  private get storage(): Storage | null {
    if (!this.isBrowser || typeof globalThis.localStorage === 'undefined') return null;
    return globalThis.localStorage;
  }

  /** Vérification côté client à partir des permissions déjà chargées — voir AuthorizationService.checkPermission pour une vérification serveur ponctuelle. */
  hasPermission(code: string): boolean {
    return this.permissionsSignal()?.permissions.some((permission) => permission.code === code) ?? false;
  }

  private completeSessionRestore(): void {
    if (this.sessionRestoredSubject.closed) return;
    this.sessionRestoredSubject.next(true);
    this.sessionRestoredSubject.complete();
  }

  /** identifier = email ou téléphone. Stocke le JWT puis charge l'utilisateur courant. */
  login(identifier: string, password: string): Observable<User> {
    return this.http
      .post<TokenResponse>(`${this.baseUrl}/login`, { identifier, password })
      .pipe(
        tap((res) => this.storeToken(res.accessToken)),
        switchMap(() => this.fetchCurrentUser()),
      );
  }

  logout(): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/logout`, {}).pipe(
      catchError(() => EMPTY),
      finalize(() => this.clearSession()),
    );
  }

  /** Renouvelle le token courant via POST /auth/refresh-token. Retourne le nouvel accessToken. */
  refreshToken(): Observable<string> {
    return this.http.post<TokenResponse>(`${this.baseUrl}/refresh-token`, {}).pipe(
      tap((res) => this.storeToken(res.accessToken)),
      map((res) => res.accessToken),
    );
  }

  /** Termine la session localement sans appeler le backend (utilisé quand le rafraîchissement du token échoue). */
  endSessionLocally(): void {
    this.clearSession();
  }

  register(payload: RegisterPayload): Observable<ApiMessageResponse> {
    return this.http.post<ApiMessageResponse>(`${this.baseUrl}/register`, payload);
  }

  verifyAccount(token: string): Observable<ApiMessageResponse> {
    return this.http.post<ApiMessageResponse>(`${this.baseUrl}/verify`, { token });
  }

  /** Redemande un code d'activation par email. Réponse volontairement neutre côté backend. */
  resendActivationToken(email: string): Observable<ApiMessageResponse> {
    return this.http.post<ApiMessageResponse>(`${this.baseUrl}/activation-token/resend`, { email });
  }

  /** Vérifie un token reçu par lien email (query param) sans le consommer. */
  activationTokenStatus(token: string): Observable<ActivationTokenCheck> {
    return this.http.get<ActivationTokenCheck>(`${this.baseUrl}/activation-token/status`, {
      params: { token },
    });
  }

  /** Vérifie un token saisi manuellement dans un formulaire sans le consommer. */
  verifyActivationTokenBeforeSubmit(token: string): Observable<ActivationTokenCheck> {
    return this.http.post<ActivationTokenCheck>(`${this.baseUrl}/activation-token/verify-before-submit`, {
      token,
    });
  }

  /** Déclenche l'envoi d'un code de réinitialisation. Réponse volontairement neutre côté backend. */
  forgotPassword(identifier: string): Observable<ApiMessageResponse> {
    return this.http.post<ApiMessageResponse>(`${this.baseUrl}/password/forgot`, { identifier });
  }

  /** Change le mot de passe avec le code reçu (usage unique). Ne connecte pas automatiquement. */
  resetPassword(token: string, newPassword: string): Observable<ApiMessageResponse> {
    return this.http.post<ApiMessageResponse>(`${this.baseUrl}/password/reset`, {
      token,
      newPassword,
    });
  }

  /** Résumé sécurité du compte connecté (vérifications, sessions actives, dernière connexion…). */
  securitySummary(): Observable<SecuritySummary> {
    return this.http.get<SecuritySummary>(`${this.baseUrl}/me/security-summary`);
  }

  /** Change le mot de passe du compte connecté. Le backend révoque les sessions existantes. */
  changePassword(currentPassword: string, newPassword: string): Observable<ApiMessageResponse> {
    return this.http.patch<ApiMessageResponse>(`${this.baseUrl}/me/password`, {
      currentPassword,
      newPassword,
    });
  }

  /** Étape 1 du changement d'email : envoie un code de vérification au nouvel email. */
  requestEmailChange(newEmail: string): Observable<ApiMessageResponse> {
    return this.http.patch<ApiMessageResponse>(`${this.baseUrl}/me/email`, { newEmail });
  }

  /** Étape 2 : valide le code reçu et applique le nouvel email. */
  verifyEmailChange(token: string, newEmail: string): Observable<ApiMessageResponse> {
    return this.http.post<ApiMessageResponse>(`${this.baseUrl}/me/email/verify`, { token, newEmail });
  }

  /**
   * Charge l'utilisateur courant puis ses droits (authorization-service). Si authorization-service
   * est indisponible, on retombe silencieusement sur les rôles portés par le JWT (comportement
   * précédent) plutôt que de bloquer la connexion pour un service annexe.
   */
  fetchCurrentUser(): Observable<User> {
    return this.http.get<BootstrapResponse>(`${this.baseUrl}/me/bootstrap`).pipe(
      switchMap((bootstrap) =>
        this.authorizationService.myPermissions().pipe(
          catchError(() => of(null)),
          tap((permissions) => this.permissionsSignal.set(permissions)),
          map((permissions) => toUser(bootstrap, permissions ? Array.from(permissions.roles) : (bootstrap.roles ?? []))),
        ),
      ),
      tap((user) => this.currentUserSignal.set(user)),
    );
  }

  /** Restaure la session au démarrage si un token est présent (navigateur uniquement). */
  restoreSession(): Observable<unknown> {
    if (!this.storage?.getItem(TOKEN_KEY)) {
      this.completeSessionRestore();
      return EMPTY;
    }

    return this.fetchCurrentUser().pipe(
      catchError(() => {
        this.clearSession();
        return EMPTY;
      }),
      finalize(() => this.completeSessionRestore()),
    );
  }

  private storeToken(accessToken: string): void {
    this.storage?.setItem(TOKEN_KEY, accessToken);
  }

  private clearSession(): void {
    this.storage?.removeItem(TOKEN_KEY);
    this.currentUserSignal.set(null);
    this.permissionsSignal.set(null);
  }
}
